import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useBodyScrollLock } from "./shared";

// Laporan email yang dipakai lebih dari satu student (data lama dari
// spreadsheet). Hanya untuk dibaca — perbaikannya dilakukan manual oleh admin
// di Edit Student. Tidak ada data yang diubah dari sini.
const csvCell = (value) => {
  const text = String(value ?? "");
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const downloadCsv = (groups) => {
  const header = ["Email", "Student row ID", "Student ID", "Name", "Batch", "Program", "Used by portal login / QR", "Package records", "Active QR", "Likely case"];
  const lines = [header.map(csvCell).join(",")];
  for (const g of groups) {
    for (const s of g.students) {
      lines.push(
        [g.email, s.studentRowId, s.studentId, s.name, s.batch, s.program, s.usedByPortal ? "yes" : "no", s.packageRecords, s.hasActiveQr ? "yes" : "no", g.sameStudentId ? "duplicate record (same Student ID)" : g.sameName ? "same person registered again" : "different people"]
          .map(csvCell)
          .join(","),
      );
    }
  }
  // BOM supaya Excel membaca UTF-8 dengan benar.
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `duplicate-student-emails-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const DuplicateEmailsModal = ({ data, onClose, onOpenStudent }) => {
  useBodyScrollLock();
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="adm-modal-backdrop" onClick={onClose}>
      <div className="adm-modal spk-detail" style={{ width: "820px" }} role="dialog" aria-modal="true" aria-labelledby="spk-dup-title" onClick={(e) => e.stopPropagation()}>
        <div className="adm-modal-header">
          <div>
            <h5 id="spk-dup-title" className="adm-card-title" style={{ fontSize: "17px" }}>
              Shared student emails
            </h5>
            <p className="adm-card-subtitle">
              {data.totalGroups} email address(es) are used by {data.totalStudents} student records. One email should belong to one student —
              these come from the old spreadsheet data and need to be corrected manually in Edit Student.
            </p>
          </div>
          <button type="button" className="adm-modal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="adm-modal-body">
          <div className="spk-callout is-info">
            <i className="bi bi-info-circle" aria-hidden="true"></i>
            <p className="spk-callout-text" style={{ margin: 0 }}>
              Nothing here is changed automatically. Package records and QR Codes belong to each student record, not to the email. The student
              portal (login and QR Code) currently uses the record marked <b>Portal</b>.
            </p>
          </div>
          {data.groups.map((g) => (
            <div key={g.email} className="spk-dup-group">
              <p className="spk-dup-email">
                {g.email}
                {g.sameStudentId ? (
                  <span className="spk-tag">same Student ID — likely a duplicate record</span>
                ) : g.sameName ? (
                  <span className="spk-tag">same name — likely the same person registered again</span>
                ) : (
                  <span className="spk-tag">different people</span>
                )}
              </p>
              <div className="adm-table-wrap">
                <table className="adm-table adm-table--compact">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Batch</th>
                      <th>Program</th>
                      <th>Package</th>
                      <th aria-label="Actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.students.map((s) => (
                      <tr key={s.studentRowId}>
                        <td>
                          <div className="adm-cell-title">
                            {s.name}
                            {s.usedByPortal && <span className="spk-tag">Portal</span>}
                          </div>
                          <div className="adm-cell-meta">
                            {s.studentId} · record #{s.studentRowId}
                          </div>
                        </td>
                        <td>{s.batch || "—"}</td>
                        <td>{s.program}</td>
                        <td>
                          {s.packageRecords ? `${s.packageRecords} record(s)` : <span className="spk-dim">none</span>}
                          {s.hasActiveQr && <div className="adm-cell-meta">QR active</div>}
                        </td>
                        <td className="text-end text-nowrap">
                          <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => onOpenStudent(s.studentRowId)}>
                            Package
                          </button>
                          <a className="adm-btn adm-btn--ghost adm-btn--sm" href={`/studentdatapage/${s.studentRowId}`} target="_blank" rel="noreferrer">
                            Edit Student
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
        <div className="adm-modal-footer">
          <button type="button" className="adm-btn adm-btn--secondary" onClick={() => downloadCsv(data.groups)}>
            <i className="bi bi-download"></i> Download CSV
          </button>
          <button type="button" className="adm-btn adm-btn--primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DuplicateEmailsModal;
